import axios, { AxiosError } from 'axios'
import type { ApiError } from './types'

const TOKEN_KEY = 'wajid.token'

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  headers: { 'Content-Type': 'application/json' },
})

export function storedToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

export function storeToken(token: string | null) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token)
    else localStorage.removeItem(TOKEN_KEY)
  } catch {
    // A locked-down browser is not a reason to fail; the session simply lasts
    // until the tab closes.
  }
}

api.interceptors.request.use((config) => {
  const token = storedToken()
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

let onUnauthorised: (() => void) | null = null

export function setUnauthorisedHandler(handler: () => void) {
  onUnauthorised = handler
}

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    // 401 means the token is gone or stale and the PIN screen is the answer.
    // 403 means this account genuinely may not, and signing in again would
    // change nothing - so it is left to the screen to explain.
    if (error.response?.status === 401) {
      storeToken(null)
      onUnauthorised?.()
    }
    return Promise.reject(error)
  },
)

/** The message a person should actually read when a request fails. */
export function errorMessage(error: unknown): string {
  const axiosError = error as AxiosError<ApiError>
  const body = axiosError?.response?.data

  if (body?.fieldErrors?.length) {
    return body.fieldErrors.map((f) => f.message).join('، ')
  }
  if (body?.message) return body.message
  if (axiosError?.code === 'ERR_NETWORK') {
    return 'سرور سے رابطہ نہیں ہو رہا۔ انٹرنیٹ یا سرور چیک کریں۔'
  }
  return 'کچھ گڑبڑ ہوئی۔ دوبارہ کوشش کریں۔'
}
